import { useAppDispatch, useAppSelector } from '@/state/store';
import { useRef, useState } from 'react';
import { createCardThunk, toggleDeckEnabled, setShowDecksView } from '@/state/features/app';
import useDecks from '@/hooks/useDecks';
import { CardDeck } from '@common/models/cards';

import styles from './styles.module.scss';
import DeckCSVUpload from '@/components/Decks/DeckCSVUpload';
import { X } from 'lucide-react';

interface DecksProps {}

export default function Decks({}: DecksProps) {
    const decks = useAppSelector((state) => state.app.decks) || [];
    const cards = useAppSelector((state) => state.app.cardsByDeckId) || {};
    const enabledDecks = useAppSelector((state) => state.app.enabledDecks) || [];
    const dispatch = useAppDispatch();
    const { remove } = useDecks();

    async function handleRemoveDeck(deckId: string, deckName: string) {
        if (confirm(`Are you sure you want to delete the deck "${deckName}"?`)) {
            await remove(deckId);
        }
    }

    function handleToggleDeck(deckId: string) {
        dispatch(toggleDeckEnabled(deckId));
    }

    const handleClose = () => {
        dispatch(setShowDecksView(false));
    };

    return (
        <div className={styles.Decks}>
            <div className={styles.decksHeader}>
                <h2>Decks</h2>
                <button onClick={handleClose} className={styles.closeButton}>
                    <X color="var(--primary-foreground)" />
                </button>
            </div>
            {decks.map((deck) => (
                <Deck
                    key={deck.id}
                    deck={deck}
                    cards={cards}
                    handleRemoveDeck={handleRemoveDeck}
                    isEnabled={enabledDecks.includes(deck.id)}
                    onToggleEnabled={handleToggleDeck}
                />
            ))}

            <hr />
            <AddDeck />
        </div>
    );
}

function Deck({
    deck,
    cards,
    handleRemoveDeck,
    isEnabled,
    onToggleEnabled,
}: {
    deck: CardDeck;
    cards: Record<string, any[]>;
    handleRemoveDeck: (deckId: string, deckName: string) => void;
    isEnabled: boolean;
    onToggleEnabled: (deckId: string) => void;
}) {
    const [showAddCard, setShowAddCard] = useState(false);

    return (
        <div className={styles.deck}>
            <div className={styles.deckHeader}>
                <div>
                    <h3>{deck.name}</h3>
                    <label className={styles.toggleSwitch}>
                        <input type="checkbox" checked={isEnabled} onChange={() => onToggleEnabled(deck.id)} />
                        <span className={styles.slider}></span>
                        Enabled
                    </label>
                </div>
                <div>
                    <button onClick={() => handleRemoveDeck(deck.id, deck.name)} className={styles.removeButton}>
                        Delete Deck
                    </button>
                    <button onClick={() => setShowAddCard((prev) => !prev)} className={styles.removeButton}>
                        + Card
                    </button>
                </div>
            </div>
            <DeckCSVUpload deck_id={deck.id} />
            {showAddCard && <AddCard deckId={deck.id} />}
            <p>{deck.description}</p>
            <p>{cards[deck.id]?.length || 0} cards</p>
            {/* 
            {isEnabled && (
                <div className={styles.cards}>
                    {cards[deck.id]?.map((card) => (
                        <div key={card.id} className={styles.card}>
                            {card.front} - {card.back}
                        </div>
                    ))}
                </div>
            )} */}
        </div>
    );
}

function AddDeck() {
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const { create } = useDecks();

    async function handleAddDeck() {
        if (name.trim() === '') {
            alert('Please enter a valid deck name.');
            return;
        }
        setLoading(true);
        await create(name.trim());
        setName('');
        setLoading(false);
    }

    return (
        <div className={styles.AddDeck}>
            <input type="text" placeholder="Deck Name" value={name} onChange={(e) => setName(e.target.value)} />

            <button onClick={handleAddDeck} className={styles.addDeckButton} disabled={loading}>
                Add Deck
            </button>
        </div>
    );
}

function AddCard({ deckId }: { deckId: string }) {
    const [front, setFront] = useState('');
    const [back, setBack] = useState('');
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();
    const firstInputRef = useRef<HTMLInputElement>(null);

    async function handleAddCard() {
        if (front.trim() === '' || back.trim() === '') {
            alert('Please enter both front and back text for the card.');
            return;
        }
        setLoading(true);
        try {
            await dispatch(
                createCardThunk({
                    front: front.trim(),
                    back: back.trim(),
                    deck_id: deckId,
                }),
            );
            setFront('');
            setBack('');
        } catch (error) {
            console.error('Failed to create card:', error);
            alert('Failed to create card. Please try again.');
        }
        setLoading(false);
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            handleAddCard();
        }
    }

    return (
        <div className={styles.AddCard}>
            <input
                ref={firstInputRef}
                type="text"
                placeholder="Front of card"
                value={front}
                onChange={(e) => setFront(e.target.value)}
                onKeyDown={handleKeyDown}
            />
            <input
                type="text"
                placeholder="Back of card"
                value={back}
                onChange={(e) => setBack(e.target.value)}
                onKeyDown={handleKeyDown}
            />
            <button onClick={handleAddCard} className={styles.addCardButton} disabled={loading}>
                Add Card
            </button>
        </div>
    );
}
